import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const frozenEmbryoTransfer: ContentBlock<"procedure"> = {
  id: makeId("proc-frozen-embryo-transfer"),
  kind: "procedure",
  tags: ["ivf", "fet", "during-cycle"],
  title: "Frozen Embryo Transfer (FET)",
  subtitle: "Transfer of a previously frozen embryo into a prepared uterus",
  body: {
    procedureName: "Frozen Embryo Transfer (FET)",
    category: "transfer",
    description:
      "Transfer of a previously frozen embryo into a prepared uterus. Can be done on a medicated or natural cycle.",
    howItWorks:
      "The preparation may be natural, modified-natural, or medicated. Your clinic uses ultrasound, laboratory results, cycle timing, and your history to decide whether the endometrium is ready; there is no single thickness or pattern that applies to everyone. In a medicated cycle, the clinic's order controls the route and timing of estrogen and progesterone. Thaw and transfer outcomes vary with the embryo, laboratory, and vitrification method, and the embryology team confirms what happened for the selected embryo.",
    duration:
      "The procedure and preparation schedule vary by protocol and clinic",
    anesthesia: "none",
    recoveryTime:
      "Follow your clinic's activity and symptom instructions after the transfer; routine bed rest is not supported by evidence",
    costRange: { low: 3000, high: 6000 },
    currency: "USD",
    whatToExpect: [
      "A preparation plan and monitoring schedule based on your clinic's protocol",
      "Medication instructions, if prescribed, from your clinic or pharmacy label",
      "A transfer using the clinic's selected catheter and imaging approach",
      "An embryology update about the thaw and whether transfer went ahead",
      "A result-testing date and medication plan from your care team",
    ],
    prepInstructions: [
      "Follow the written medication and appointment instructions from your clinic; do not start, stop, or change treatment from this article",
      "Ask your clinic whether a full bladder is needed and how to prepare for it",
      "Take a prescribed sedative only as directed, and arrange the support or transport your clinic requires",
      "Ask what activity and symptom guidance applies after your transfer; strict bed rest is not routinely recommended",
    ],
    results: [
      {
        label: "Thaw result",
        detail:
          "The embryology team confirms whether the selected embryo thawed and whether the planned transfer could proceed. A result for one embryo does not predict another embryo's outcome.",
      },
      {
        label: "Procedure confirmation",
        detail:
          "Your clinic records what happened during the procedure and tells you whether the planned transfer was completed.",
      },
      {
        label: "Pregnancy test timing",
        detail:
          "Your clinic sets the beta hCG date for the protocol and transfer timing. Interpret the result with your care team rather than from a fixed number in an article.",
      },
    ],
    risks: [
      "The procedure can involve pressure, cramping, spotting, or other symptoms; ask your clinic what to report urgently",
      "An embryo may not survive thaw or the planned transfer may not proceed; the laboratory will explain the specific result",
    ],
    frBody: {
      procedureName: "Transfert d'embryon congelé (TEC)",
      description:
        "Transfert d'un embryon précédemment congelé dans un utérus préparé. Peut être réalisé sur cycle médicamenteux ou naturel.",
      howItWorks:
        "La préparation peut être naturelle, modifiée-naturelle ou médicamenteuse. Votre centre utilise l'échographie, les analyses, le moment du cycle et vos antécédents pour décider si l'endomètre est prêt ; aucune épaisseur ni aucun aspect ne s'applique de la même façon à tout le monde. Pour un cycle médicamenteux, l'ordonnance du centre fixe la voie et le moment des œstrogènes et de la progestérone. Le résultat de la décongélation et du transfert dépend de l'embryon, du laboratoire et de la méthode de vitrification ; l'équipe d'embryologie vous confirme ce qui s'est passé pour l'embryon choisi.",
      duration:
        "La durée du geste et le calendrier de préparation varient selon le protocole et le centre",
      recoveryTime:
        "Suivez les consignes de votre centre concernant les activités et les symptômes après le transfert ; le repos strict au lit n'est pas étayé par les données",
      whatToExpect: [
        "Un plan de préparation et un calendrier de suivi adaptés au protocole de votre centre",
        "Les consignes de médicaments, s'ils sont prescrits, données par votre centre ou la notice de la pharmacie",
        "Un transfert avec le cathéter et la méthode d'imagerie choisis par le centre",
        "Une information de l'embryologie sur la décongélation et la réalisation éventuelle du transfert",
        "Une date de test et un plan de médicaments communiqués par votre équipe",
      ],
      prepInstructions: [
        "Suivez les consignes écrites de votre centre pour les médicaments et les rendez-vous ; ne commencez, n'arrêtez et ne modifiez pas un traitement à partir de cet article",
        "Demandez à votre centre si une vessie pleine est nécessaire et comment vous y préparer",
        "Prenez un sédatif prescrit uniquement selon les indications et prévoyez l'accompagnement ou le transport demandé par votre centre",
        "Demandez quelles consignes d'activité et de symptômes s'appliquent après le transfert ; le repos strict au lit n'est pas recommandé en routine",
      ],
      results: [
        {
          label: "Décongélation",
          detail:
            "Le centre confirme généralement le jour du transfert que l'embryon a survécu à la décongélation et peut être transféré.",
        },
        {
          label: "Confirmation du geste",
          detail:
            "Votre centre consigne le déroulement du geste et vous indique si le transfert prévu a été réalisé.",
        },
        {
          label: "Test de grossesse",
          detail:
            "Votre centre fixe la date du dosage de bêta-hCG selon le protocole et le moment du transfert. Interprétez le résultat avec votre équipe plutôt qu'à partir d'un chiffre fixe dans un article.",
        },
      ],
      risks: [
        "Le geste peut provoquer une pression, des crampes, des pertes ou d'autres symptômes ; demandez à votre centre lesquels nécessitent un avis urgent",
        "Un embryon peut ne pas survivre à la décongélation ou le transfert prévu peut ne pas avoir lieu ; le laboratoire explique le résultat précis",
      ],
    },
  },
  sources: [
    {
      title: "Transfer of Fresh versus Frozen Embryos in Ovulatory Women",
      authors: "Shi Y, Sun Y, Hao C, et al.",
      journal: "New England Journal of Medicine",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/29320646/",
      evidenceLevel: "rct",
    },
    {
      title:
        "Is frozen embryo transfer better for mothers and babies? Can cumulative meta-analysis provide a definitive answer?",
      authors:
        "Maheshwari A, Pandey S, Amalraj Raja E, Shetty A, Hamilton M, Bhattacharya S",
      journal: "Human Reproduction Update",
      year: 2018,
      url: "https://pubmed.ncbi.nlm.nih.gov/29155965/",
      evidenceLevel: "meta-analysis",
    },
  ],
};

registerBlock(frozenEmbryoTransfer);
